import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert, RefreshCw, RotateCcw, Copy } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  /** Optional custom fallback — if omitted, renders Nova-themed error UI */
  fallback?: ReactNode;
  /** Label shown in the error UI to identify which surface crashed */
  surface?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// GlobalErrorBoundary
// A class component that catches render-phase errors and shows a premium Nova
// fallback instead of a blank white screen.
// ─────────────────────────────────────────────────────────────────────────────

class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const surface = this.props.surface ?? 'Nova Workspace';
    const isDev = import.meta.env.DEV;

    const textToCopy = isDev
      ? `Error: ${error?.message ?? 'Unknown error'}\n\nComponent Stack:\n${errorInfo?.componentStack ?? 'No stack trace available'}`
      : `[VEXL Diagnostic Log]\nSurface: ${surface}\nTimestamp: ${new Date().toISOString()}\nStatus: Error captured`;
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
      })
      .catch(() => {
        // Intentionally silent — clipboard failure is benign and not logged
        // to avoid cluttering the console in production.
      });
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Structured error log
    console.error(
      `[GlobalErrorBoundary][${this.props.surface ?? 'App'}] Unhandled render error:`,
      { message: error.message, stack: error.stack, componentStack: errorInfo.componentStack }
    );

    // Dynamic import to avoid early startup initialization race conditions
    import('@/lib/observability')
      .then(({ captureError }) => {
        captureError(error, {
          surface: this.props.surface ?? 'App',
          componentStack: errorInfo.componentStack || '',
        });
      })
      .catch((err) => {
        console.warn('[ErrorBoundary] Failed to log error to observability pipeline:', err);
      });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { error, errorInfo } = this.state;
    const surface = this.props.surface ?? 'Nova Workspace';

    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center p-6 font-sans">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-md"
        >
          {/* Card */}
          <div className="bg-[#0b0c10] border border-slate-800/80 rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.4)]">
            
            {/* Header */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-12 h-12 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center mb-4">
                <ShieldAlert size={20} className="text-slate-400" />
              </div>
              <h2 className="text-xl font-bold text-white tracking-tight">
                Application Error
              </h2>
              <p className="text-xs text-slate-400 mt-1.5 max-w-[280px] leading-relaxed">
                An unexpected error occurred in the <span className="text-slate-200 font-semibold">{surface}</span> interface.
              </p>
            </div>

            {/* Error message */}
            <div className="bg-[#02040a] border border-slate-900 rounded-lg p-4 mb-6 font-mono relative">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                  Error Log
                </p>
                <button
                  onClick={this.handleCopyError}
                  className="text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1.5 cursor-pointer focus:outline-none font-sans font-medium"
                  title="Copy error details to clipboard"
                >
                  <Copy size={11} />
                  <span>{this.state.copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-xs text-slate-300 break-words leading-relaxed select-text">
                {import.meta.env.DEV
                  ? (error?.message ?? 'An unexpected error occurred.')
                  : (() => {
                      // In production, sanitize the raw exception message to prevent
                      // leaking internal file paths, node_modules references, or
                      // sensitive implementation details embedded by library errors.
                      const raw = error?.message ?? '';
                      const sanitized = raw
                        .replace(/(?:\/[^\s/]+)+\/[^\s/]+\.[a-z]+/gi, '[internal]')
                        .replace(/node_modules\/[^\s]*/gi, '[dependency]')
                        .slice(0, 120);
                      return sanitized || 'An unexpected error occurred. Please try again.';
                    })()
                }
              </p>
              
              {import.meta.env.DEV && errorInfo?.componentStack && (
                <details className="mt-3 border-t border-slate-900/60 pt-3">
                  <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-400 transition-colors focus:outline-none">
                    Stack Trace
                  </summary>
                  <pre className="mt-2 text-[9px] text-slate-600 overflow-auto max-h-32 leading-relaxed whitespace-pre-wrap select-text">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white text-slate-950 text-xs font-semibold hover:bg-slate-100 transition-colors"
              >
                <RefreshCw size={13} />
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-transparent border border-slate-800 text-slate-300 text-xs font-semibold hover:bg-white/5 transition-colors"
              >
                <RotateCcw size={13} />
                Reload Page
              </button>
            </div>
          </div>

          {/* Footer / System Status */}
          <div className="text-center mt-5 text-[10px] text-slate-500 flex flex-col items-center gap-1">
            <p>VEXL System Shield Active</p>
            <p>
              If this persists, contact support at{' '}
              <a href="mailto:vexl.gis@gmail.com" className="text-slate-400 hover:text-white transition-colors underline">
                vexl.gis@gmail.com
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }
}

export default GlobalErrorBoundary;
